/**
 * Map PATCH /vehicles/:id failures to a sentence the workshop can act on (#2122).
 *
 * 403 is a missing `vehicles.update` grant. A 409 is a duplicate plate only when
 * the backend body names a registration collision; any other 409 (idempotency,
 * unknown outcome) is a generic conflict even if the editor has a plate. 412 /
 * `STALE_ROW` means someone else changed the card. Never return the HTTP status
 * line.
 */

import type { TFunction } from 'i18next';
import { ApiError } from '@/lib/api';
import { isTemporarilyUnavailable } from '@/lib/describeApiError';

interface VehicleSaveNotify {
  error: (title: string, message: string) => void;
}

function errorBody(error: ApiError): { code?: unknown; message?: unknown } {
  const { data } = error;
  return typeof data === 'object' && data !== null
    ? (data as { code?: unknown; message?: unknown })
    : {};
}

function isStaleConflict(error: ApiError): boolean {
  if (error.status === 412) return true;
  return errorBody(error).code === 'STALE_ROW';
}

function isDuplicateRegistration(error: ApiError): boolean {
  if (error.status !== 409 || isStaleConflict(error)) return false;
  const { code, message } = errorBody(error);
  if (typeof code === 'string' && /idempotenc/i.test(code)) return false;
  return typeof message === 'string' && /registration/i.test(message);
}

export function vehicleSaveErrorMessage(
  error: unknown,
  t: TFunction,
  tc: TFunction,
  options?: { registrationNumber?: string },
): string {
  if (isTemporarilyUnavailable(error)) {
    return tc('errors.temporarilyUnavailable');
  }

  if (error instanceof ApiError) {
    if (error.status === 403) {
      return tc('errors.forbidden');
    }
    if (isStaleConflict(error)) {
      return tc('errors.conflict');
    }
    if (error.status === 409) {
      if (isDuplicateRegistration(error)) {
        return t('form.duplicateRegistration', {
          reg: options?.registrationNumber ?? '',
        });
      }
      return tc('errors.conflict');
    }
    if (error.status === 401) {
      return tc('errors.unauthorized');
    }
    if (error.status === 404) {
      return tc('errors.notFound');
    }
    if (error.status === 423) {
      return tc('errors.locked');
    }
    if (error.status === 429) {
      return tc('errors.tooManyRequests');
    }
    if (error.status >= 500) {
      return tc('errors.serverError');
    }
  }

  if (error instanceof TypeError) {
    return tc('errors.network');
  }

  return t('form.saveFailed');
}

export function notifyVehicleSaveError(
  error: unknown,
  notify: VehicleSaveNotify,
  t: TFunction,
  tc: TFunction,
  options?: { registrationNumber?: string },
): void {
  notify.error(tc('error'), vehicleSaveErrorMessage(error, t, tc, options));
}
